/**
 * Inventario API module.
 *
 * Covers the full inventory lifecycle: fetch, create, update, delete,
 * movement, filter, hide, and image management.
 *
 * All functions delegate to `apiFetch`, which handles auth headers,
 * JSON parsing and Ant Design error/success toasts automatically.
 */

import { apiFetch } from "./apiClient";
import { Inventory } from "../interfaces/api.interfaces";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InventarioFetchBody {
  propiedades?: number[];
  bodegas?: number[];
  bodeguitas?: number[];
  [key: string]: any;
}

/** Filtros de la bitácora de movimientos (round 2026-08-06). Todos opcionales. */
export interface MovimientosFiltros {
  /** `YYYY-MM-DD`. */
  desde?: string;
  /** `YYYY-MM-DD`. */
  hasta?: string;
  tipo?: "compra" | "movimiento";
  /** Categoría financiera; `"sin"` aísla las que no tienen. */
  categoria?: string;
  /** Una existencia concreta (fila de `inventario`). */
  inventarioId?: number;
  ubicacionTipo?: "bodegas" | "bodeguitas" | "propiedades";
  ubicacionId?: number;
  /** Texto libre: producto, ubicaciones, o `#123` por código de transacción. */
  q?: string;
  /** Dirección sobre la fecha. Parámetro viejo; lo reemplaza `ordenarPor`. */
  orden?: "asc" | "desc";
  /**
   * Orden por columna, resuelto en el SERVIDOR contra la lista blanca de
   * `backend/src/utils/movimientosFiltro.js`. Con paginación server-side,
   * ordenar en el navegador ordenaría la página y no la bitácora.
   */
  ordenarPor?: string;
  ordenDireccion?: "asc" | "desc";
  page?: number;
  size?: number;
}

export interface MovePayload {
  productId: number;
  fromType: string;
  fromId: number;
  toType: string;
  toId: number;
  cantidad: number;
  [key: string]: any;
}

// ─── inventarioApi ────────────────────────────────────────────────────────────

export const inventarioApi = {
  /** POST /inventario — fetch inventory (body filters which locations to include). */
  getAll: (body: InventarioFetchBody = {}): Promise<any | null> =>
    apiFetch("/inventario", {
      method: "POST",
      body: JSON.stringify(body),
      errorMsg: "Error al obtener el inventario",
    }),

  /** GET /inventario/marcas — all product brands. */
  getMarcas: (): Promise<any | null> =>
    apiFetch("/inventario/marcas", {
      errorMsg: "Error al obtener marcas",
    }),

  /** GET /inventario/distinct-all — unique values per text field for autocomplete. */
  getDistinctAll: (): Promise<any | null> =>
    apiFetch("/inventario/distinct-all", {
      errorMsg: "Error al obtener sugerencias",
    }),

  /** POST /inventario/filter — filtered inventory search. */
  filter: (body: Record<string, any>): Promise<any | null> =>
    apiFetch("/inventario/filter", {
      method: "POST",
      body: JSON.stringify(body),
      errorMsg: "Error al filtrar el inventario",
    }),

  /** POST /inventario/nuevo-item — create a product. */
  create: (payload: Partial<Inventory>): Promise<any | null> =>
    apiFetch("/inventario/nuevo-item", {
      method: "POST",
      body: JSON.stringify(payload),
      successMsg: "Producto creado correctamente",
      errorMsg: "Error al crear el producto",
    }),

  /** PUT /inventario/:id — update a product. */
  update: (id: number, payload: Partial<Inventory>): Promise<any | null> =>
    apiFetch(`/inventario/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
      successMsg: "Producto actualizado correctamente",
      errorMsg: "Error al actualizar el producto",
    }),

  /** DELETE /inventario/:id — delete a product. */
  delete: (id: number): Promise<any | null> =>
    apiFetch(`/inventario/${id}`, {
      method: "DELETE",
      successMsg: "Producto eliminado correctamente",
      errorMsg: "Error al eliminar el producto",
    }),

  /**
   * DELETE /inventario/:id — variante silenciosa (sin toast individual).
   * Usada por el flujo de borrado masivo, donde mostramos UN solo
   * `message.success` agregado tras `Promise.all`. Si dejamos `successMsg`,
   * se apilan N toasts iguales.
   */
  deleteSilent: (id: number): Promise<any | null> =>
    apiFetch(`/inventario/${id}`, {
      method: "DELETE",
      errorMsg: "Error al eliminar el producto",
    }),

  /** POST /inventario/movimiento — move units between locations. */
  move: (payload: MovePayload): Promise<any | null> =>
    apiFetch("/inventario/movimiento", {
      method: "POST",
      body: JSON.stringify(payload),
      successMsg: "Movimiento registrado correctamente",
      errorMsg: "Error al registrar el movimiento",
    }),

  /**
   * POST /inventario/similares-compra — «¿alguno de estos es el mismo?».
   *
   * Busca en TODO el inventario, no solo en el destino, y cada candidato viene
   * con `accion`: `"fusion"` (suma stock a una fila que ya está en el destino)
   * o `"nueva_existencia"` (nace una fila en el destino colgada de la misma
   * ficha). El backend calcula esa decisión con el mismo helper que ejecuta la
   * compra, así que el modal no la reimplementa.
   *
   * `esPack`/`unidadesPorPack` describen el formato de ESTA compra: sin ellos
   * el backend no puede saber si los stocks se pueden sumar y anunciaría una
   * fusión que después no ocurre.
   *
   * `incluirId` es el producto que ya se eligió en «¿ya lo compraste antes?».
   * El backend garantiza devolverlo aunque la búsqueda difusa no lo encuentre:
   * de esta lista sale el vínculo a la ficha, y si el elegido falta, la compra
   * nace como un producto separado — el duplicado que el buscador vino a evitar.
   */
  getSimilaresCompra: (body: {
    nombre: string;
    ubicacionTipo: string;
    ubicacionId: number;
    esPack?: boolean;
    unidadesPorPack?: number;
    incluirId?: number;
  }): Promise<any | null> =>
    apiFetch("/inventario/similares-compra", {
      method: "POST",
      body: JSON.stringify(body),
      errorMsg: "Error al buscar productos similares en el destino",
    }),

  /** POST /inventario/movimiento/similar-product — find compatible products for merge. */
  getSimilar: (body: Record<string, any>): Promise<any | null> =>
    apiFetch("/inventario/movimiento/similar-product", {
      method: "POST",
      body: JSON.stringify(body),
      errorMsg: "Error al buscar productos similares",
    }),

  /**
   * POST /inventario/:id/regenerar-flujos — regenera (upsert idempotente, no
   * destructivo) los flujos derivados FALTANTES del producto. Hoy recrea la
   * RECUPERACIÓN (cobro al cliente) que se haya borrado por error desde
   * /flujos, desde la transacción de inventario. No duplica si ya existe.
   * Decisión #8 del modelo de inventario. Admin-only.
   */
  regenerarFlujos: (
    id: number,
  ): Promise<{
    success: boolean;
    message?: string;
    data?: {
      recuperacionCreada: boolean;
      recuperacionFaltaba: boolean;
      reembolsoPresente: boolean;
    };
  } | null> =>
    apiFetch(`/inventario/${id}/regenerar-flujos`, {
      method: "POST",
      errorMsg: "Error al regenerar los flujos de inventario",
    }),

  /**
   * GET /inventario/:id/impacto-eliminacion — preview de TODO lo que
   * borraría eliminar el producto (transacciones, cobros al cliente,
   * reembolsos, historial, meses cerrados afectados). Solo lectura;
   * alimenta la doble confirmación explícita.
   */
  getImpactoEliminacion: (id: number): Promise<any | null> =>
    apiFetch(`/inventario/${id}/impacto-eliminacion`, {
      errorMsg: "Error al calcular el impacto de la eliminación",
    }),

  // ── Transacciones editables (round 2026-07-26) ───────────────────────────

  /**
   * GET /inventario/transacciones/:id/impacto — qué toca corregir esta
   * transacción: flujos vinculados (cobro al cliente / reembolso), propiedad
   * involucrada y si el mes ya está CERRADO para el cliente (advertencia).
   */
  getTransaccionImpacto: (id: number): Promise<any | null> =>
    apiFetch(`/inventario/transacciones/${id}/impacto`, {
      errorMsg: "Error al calcular el impacto de la transacción",
    }),

  /**
   * PATCH /inventario/transacciones/:id — corrige cantidades/precios de una
   * transacción histórica (errores de tipeo). El monto se recalcula
   * server-side y los flujos vinculados se sincronizan (el reembolso sigue
   * el costo corregido y re-pende si estaba pagado; el cobro al cliente solo
   * si nunca fue editado a mano). Sin toast de éxito acá: el caller muestra
   * el `message` detallado de la respuesta (qué se sincronizó exactamente).
   */
  updateTransaccion: (
    id: number,
    body: {
      cantidad_unidades?: number;
      cantidad_packs?: number;
      unidades_por_pack?: number;
      precio_por_unidad?: number;
      precio_por_pack?: number;
      es_pack?: boolean;
    },
  ): Promise<{ success: boolean; message?: string; data?: any; cascade?: any } | null> =>
    apiFetch(`/inventario/transacciones/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      errorMsg: "Error al corregir la transacción",
    }),

  /**
   * DELETE /inventario/transacciones/:id — borra UNA transacción con su
   * cascada de flujos (cobro al cliente / reembolso al tercero).
   *
   * Es el ÚNICO camino para eliminar una transacción HUÉRFANA (aquella cuyo
   * producto ya no existe): `delete(id)` borra desde el producto y devuelve
   * 404 cuando la fila de inventario no está. Sin toast de éxito acá — el
   * caller muestra el `message` del backend, que detalla qué se borró y si
   * tocó un mes ya cerrado para el cliente.
   */
  deleteTransaccion: (
    id: number,
  ): Promise<{ success: boolean; message?: string; data?: any } | null> =>
    apiFetch(`/inventario/transacciones/${id}`, {
      method: "DELETE",
      errorMsg: "Error al eliminar la transacción",
    }),

  /**
   * POST /inventario/unificar — «estos son el mismo producto».
   *
   * Las existencias NO se mueven ni se suman: cada una conserva su lugar, su
   * stock y su precio. Lo que cambia es que pasan a compartir UNA ficha, así
   * que editarla las alcanza a todas. `principalId` decide qué ficha queda.
   */
  unificar: (
    ids: number[],
    principalId: number,
  ): Promise<{ success: boolean; message?: string; data?: any } | null> =>
    apiFetch("/inventario/unificar", {
      method: "POST",
      body: JSON.stringify({ ids, principalId }),
      errorMsg: "Error al unificar los productos",
      errorAsModal: true,
    }),

  /**
   * GET /inventario/:id/ubicaciones — dónde MÁS está este mismo producto.
   *
   * Responde "¿cuánto tengo de esto en total, y repartido cómo?", que el
   * listado por sí solo no puede: cada fila es un lugar.
   */
  getUbicaciones: (id: number): Promise<any | null> =>
    apiFetch(`/inventario/${id}/ubicaciones`, {
      errorMsg: "Error al obtener las ubicaciones del producto",
    }),

  /**
   * GET /inventario/buscar?q= — productos existentes para RECOMPRAR.
   *
   * Devuelve la ficha (qué es la cosa) + foto + código. NO se prellenan los
   * precios: el precio es lo que cambia de una compra a otra, y lo que
   * corrompe las finanzas si entra mal sin que nadie lo mire. Viajan igual como
   * referencia: `precioUnidad` / `precioPack` son el precio ACTUAL del producto
   * —el promedio ponderado de todas sus compras—, no el de la última.
   */
  buscarProductos: (q: string): Promise<any | null> =>
    apiFetch(`/inventario/buscar?q=${encodeURIComponent(q)}`, {
      errorMsg: "Error al buscar productos",
    }),

  /**
   * GET /inventario/movimientos — LA BITÁCORA: todas las compras y movimientos,
   * con rango de fechas libre y filtro por lugar (que matchea ORIGEN o DESTINO),
   * producto, tipo y categoría.
   *
   * Complementa a Finanzas (mensual, ordenada por plata) y a la línea de tiempo
   * (de a un producto): es la única vista que responde "¿qué entró y salió de
   * esta propiedad?" sin saber de antemano ni el mes ni el producto.
   */
  getMovimientos: (filtros: MovimientosFiltros = {}): Promise<any | null> => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filtros)) {
      if (v === undefined || v === null || v === "") continue;
      qs.set(k, String(v));
    }
    const cola = qs.toString();
    return apiFetch(`/inventario/movimientos${cola ? `?${cola}` : ""}`, {
      errorMsg: "Error al obtener los movimientos",
    });
  },

  /**
   * GET /inventario/transacciones/huerfanas — TODAS las transacciones cuyo
   * producto ya no existe, sin importar el mes.
   *
   * El panel de Finanzas es mensual: sin esto hay que adivinar en qué mes
   * quedó cada huérfana para poder verla.
   */
  getTransaccionesHuerfanas: (): Promise<any | null> =>
    apiFetch(`/inventario/transacciones/huerfanas`, {
      errorMsg: "Error al buscar transacciones sin producto",
    }),

  /**
   * DELETE /inventario/flujos-huerfanos/:id — borra un cobro/reembolso cuya
   * transacción de inventario ya no existe.
   *
   * Es su ÚNICA puerta: /flujos oculta los derivados de inventario y su borrado
   * normal responde 409. Ver el daño inverso en docs/modulo-inventario.md §3.2.
   */
  deleteFlujoHuerfano: (id: number): Promise<any | null> =>
    apiFetch(`/inventario/flujos-huerfanos/${id}`, {
      method: "DELETE",
      successMsg: "Flujo eliminado",
      errorMsg: "Error al eliminar el flujo",
      errorAsModal: true,
    }),

  /**
   * GET /inventario/:id/historia — línea de tiempo del producto.
   *
   * Acepta ids de productos YA BORRADOS mientras quede rastro (transacciones o
   * historial): es lo que hace explicable una transacción huérfana.
   */
  getHistoria: (id: number): Promise<any | null> =>
    apiFetch(`/inventario/${id}/historia`, {
      errorMsg: "Error al obtener la historia del producto",
    }),

  /**
   * POST /inventario/:id/unhide — vuelve a mostrar un producto ocultado.
   *
   * Ocultar era irreversible desde la UI: el producto salía de la lista
   * (`oculto = false` es el filtro del listado) y con él su botón de eliminar,
   * mientras sus transacciones seguían contando en las finanzas del mes.
   */
  unhide: (id: number): Promise<any | null> =>
    apiFetch(`/inventario/${id}/unhide`, {
      method: "POST",
      successMsg: "El producto volvió a la lista",
      errorMsg: "Error al volver a mostrar el producto",
    }),

  /** POST /inventario/:id/hide — soft-hide a product with optional reason. */
  hide: (
    id: number,
    body?: { razon?: string; detalle?: string | null },
  ): Promise<any | null> =>
    apiFetch(`/inventario/${id}/hide`, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
      successMsg: "Producto ocultado",
      errorMsg: "Error al ocultar el producto",
    }),

  // ── Images ────────────────────────────────────────────────────────────────

  /** GET /inventario/:id/images */
  getImages: (id: number): Promise<any | null> =>
    apiFetch(`/inventario/${id}/images`, {
      errorMsg: "Error al obtener imágenes",
    }),

  /** POST /inventario/:id/images — upload via FormData (multipart). */
  uploadImage: (id: number, formData: FormData): Promise<any | null> =>
    apiFetch(`/inventario/${id}/images`, {
      method: "POST",
      body: formData,
      errorMsg: "Error al subir la imagen",
    }),

  /** DELETE /inventario/:id/images — delete an image by URL (query param). */
  deleteImage: (id: number, url: string): Promise<any | null> =>
    apiFetch(`/inventario/${id}/images?url=${encodeURIComponent(url)}`, {
      method: "DELETE",
      errorMsg: "Error al eliminar la imagen",
    }),
};
